import { useMemo, useEffect } from 'react';
import moment from 'moment';
import { formatMoney } from '@dill/dill-shared/dist/src';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getSupplierCreditApplicationsReport } from '../../../../../redux/services/reportsService';
import { getSupplierBranches } from '../../../../../redux/services/supplierAdditionalDetailsService';
import ApplicationsTrendChart from './ApplicationsTrendChart';
import CreditAmountsChart from './CreditAmountsChart';
import TimeToDecisionChart from './TimeToDecisionChart';
import { AppButton } from '../../../../general/AppButton/AppButton';
import { APP_ENVIRONMENT } from '../../../../../utils/constants';
import SalesRepBreakdownTable from './SalesRepBreakdownTable';

interface CreditApplicationsReportsProps {
  selectedYear: string;
  filters: { [key: string]: string[] } | null;
  approvedDateRange?: { start: string; end: string };
}

function CreditApplicationsReports({
  selectedYear,
  filters,
  approvedDateRange,
}: CreditApplicationsReportsProps) {
  const dispatch = useAppDispatch();

  const { supplierCreditApplicationsAnnualReport } = useAppSelector((state) => state.reports);
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);

  function convertMilliseconds(ms: number): { value: number; unit: string } {
    const seconds = ms / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;
    const days = hours / 24;
    const months = days / 30;

    if (months >= 1) {
      return {
        value: parseFloat(months.toFixed(0)),
        unit: `Month${parseFloat(months.toFixed(0)) > 1 ? 's' : ''}`,
      };
    }
    if (days >= 1) {
      return {
        value: parseFloat(days.toFixed(0)),
        unit: `Day${parseFloat(days.toFixed(0)) > 1 ? 's' : ''}`,
      };
    }
    if (hours >= 1) {
      return {
        value: parseFloat(hours.toFixed(0)),
        unit: `Hour${parseFloat(hours.toFixed(0)) > 1 ? 's' : ''}`,
      };
    }
    if (minutes >= 1) {
      return {
        value: parseFloat(minutes.toFixed(0)),
        unit: `Minute${parseFloat(minutes.toFixed(0)) > 1 ? 's' : ''}`,
      };
    }

    return {
      value: parseFloat(seconds.toFixed(0)),
      unit: `Second${parseFloat(seconds.toFixed(0)) > 1 ? 's' : ''}`,
    };
  }

  const compileData = useMemo(() => {
    const raw = supplierCreditApplicationsAnnualReport?.compiledCreditApplicationSummary ?? [];
    const toDate = (v: any) => (v ? new Date(v) : null);

    const parseApp = (item: any) => ({
      ...item,
      createdAt: toDate(item.createdAt),
      updatedAt: toDate(item.updatedAt),
      approvedAt: toDate(item.approvedAt),
      rejectedAt: toDate(item.rejectedAt),
      submittedAt: toDate(item.submittedAt),
    });

    const matchesFilters = (application: any) => {
      if (filters) {
        const allMatch = Object.entries(filters).every(([key, values]) => {
          if (!values?.length) return true;
          const appValue = application[key];
          if (!appValue) return false;

          const formatted = String(appValue).toLowerCase().replace(/_/g, ' ');
          return values.map((v) => v.toLowerCase()).includes(formatted);
        });
        if (!allMatch) return false;
      }

      if (approvedDateRange?.start || approvedDateRange?.end) {
        const approvedAt: Date | null = application.approvedAt;
        if (!approvedAt) return false;
        const approvedMoment = moment(approvedAt);
        if (
          approvedDateRange.start &&
          approvedMoment.isBefore(moment(approvedDateRange.start, 'YYYY-MM-DD'), 'day')
        )
          return false;
        if (
          approvedDateRange.end &&
          approvedMoment.isAfter(moment(approvedDateRange.end, 'YYYY-MM-DD'), 'day')
        )
          return false;
      }

      return true;
    };

    const timeDiffHours = (start: Date | null, end: Date | null) => {
      if (!start || !end) return 0;
      return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    };

    const getMonthRange = (year: number, month: number) => {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0, 23, 59, 59);
      return { start, end };
    };

    const parsed = raw
      .filter((item) => !['DRAFT', 'SENT', 'OPENED'].includes(item.status))
      .map(parseApp);

    if (parsed.length === 0) {
      return {
        totalApplications: 0,
        approvedApplications: 0,
        newApplicationsLastMonth: 0,
        averageCreditApproved: 0,
        timeToDecision: 0,
        approvalRate: 0,
        recentDecisions: [],
        yearMonthlySummary: [],
        salesRepSummary: [],
      };
    }

    const filteredData = parsed.filter(matchesFilters);

    const totalApplications = filteredData.length;
    const approvedApplications = filteredData.filter((a) => a.status === 'APPROVED').length;

    // New applications last month
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const newApplicationsLastMonth = filteredData.filter(
      (app) => app.createdAt && app.createdAt >= oneMonthAgo
    ).length;

    // Avg credit approved
    const approvedAppsWithAmount = filteredData.filter(
      (app) => app.status === 'APPROVED' && Number(app.amountAproved) > 0
    );

    const averageCreditApproved =
      approvedAppsWithAmount.length === 0
        ? 0
        : approvedAppsWithAmount.reduce((sum, app) => sum + Number(app.amountAproved || 0), 0) /
          approvedAppsWithAmount.length;

    // Calculate average time to decision
    const decisionsWithTime = filteredData.filter(
      (app) =>
        (app.status === 'APPROVED' && (app.submittedAt || app.createdAt) && app.approvedAt) ||
        (app.status === 'REJECTED' && (app.submittedAt || app.createdAt) && app.rejectedAt)
    );

    const averageTimeToDecision =
      decisionsWithTime.length > 0
        ? decisionsWithTime.reduce((sum, app) => {
            const rawStart = app.submittedAt ?? app.createdAt;
            const rawEnd = app.status === 'APPROVED' ? app.approvedAt : app.rejectedAt;

            if (!rawStart || !rawEnd) return sum;

            const start = rawStart instanceof Date ? rawStart : new Date(rawStart);
            const end = rawEnd instanceof Date ? rawEnd : new Date(rawEnd);

            const timeDiff = end.getTime() - start.getTime();

            return sum + timeDiff;
          }, 0) / decisionsWithTime.length
        : 0;

    const COLORS = [
      'bg-purple-500',
      'bg-orange-500',
      'bg-blue-500',
      'bg-green-500',
      'bg-red-500',
      'bg-indigo-500',
    ];

    const recentDecisions = filteredData
      .filter((app) => ['APPROVED', 'REJECTED'].includes(app.status))
      .sort(
        (a, b) =>
          (b.approvedAt?.getTime() || b.rejectedAt?.getTime() || 0) -
          (a.approvedAt?.getTime() || a.rejectedAt?.getTime() || 0)
      )
      .slice(0, 5)
      .map((app, i) => {
        const words = app.companyName ? app.companyName.trim().split(' ') : [];
        const initials =
          words.length >= 2
            ? `${words[0][0]}${words[1][0]}`
            : app.companyName?.substring(0, 2) ?? '';

        return {
          id: app.id,
          company: app.companyName,
          amount: app.amountAproved,
          status: app.status === 'APPROVED' ? 'Approved' : 'Rejected',
          initials: initials.toUpperCase(),
          color: COLORS[i % COLORS.length],
        };
      });

    const targetYear = selectedYear ? parseInt(selectedYear) : new Date().getFullYear();

    const yearMonthlySummary = Array.from({ length: 12 }).map((_, month) => {
      const { start, end } = getMonthRange(targetYear, month);

      const monthApps = filteredData.filter(
        (app) => app.createdAt && app.createdAt >= start && app.createdAt <= end
      );

      const monthApproved = monthApps.filter((a) => a.status === 'APPROVED');
      const monthRejected = monthApps.filter((a) => a.status === 'REJECTED');

      const approvedWithAmounts = monthApproved.filter((app) => Number(app.amountAproved) > 0);

      const totalCreditApproved = approvedWithAmounts.reduce(
        (sum, app) => sum + Number(app.amountAproved || 0),
        0
      );

      const avgCreditApproved =
        approvedWithAmounts.length === 0 ? 0 : totalCreditApproved / approvedWithAmounts.length;

      const monthDecisions = monthApps.filter((app) => {
        const startT = app.submittedAt ?? app.createdAt;
        const endT = app.status === 'APPROVED' ? app.approvedAt : app.rejectedAt;
        return startT && endT;
      });

      const avgDecision =
        monthDecisions.length === 0
          ? 0
          : monthDecisions.reduce((sum, app) => {
              const startT = app.submittedAt ?? app.createdAt;
              const endT = app.status === 'APPROVED' ? app.approvedAt : app.rejectedAt;
              return sum + timeDiffHours(startT, endT);
            }, 0) / monthDecisions.length;

      const monthName = start.toLocaleString('default', { month: 'long' });

      return {
        id: `${targetYear}-${monthName.substring(0, 3).toUpperCase()}`,
        month: String(month + 1),
        year: String(targetYear),
        monthName,
        totalApplications: monthApps.length,
        approvedApplications: monthApproved.length,
        rejectedApplications: monthRejected.length,
        totalCreditApproved: Math.round(totalCreditApproved),
        averageCreditApproved: Math.round(avgCreditApproved),
        averageTimeToDecision: Math.round(avgDecision),
      };
    });

    // Build email -> name lookup from user's branches
    const salesRepIdToName = new Map<string, string>();
    for (const branch of supplierBranches) {
      for (const rep of branch.salesRep ?? []) {
        if (rep.id) {
          salesRepIdToName.set(rep.id, rep?.name ?? '');
        }
      }
    }

    // Sales rep breakdown
    const salesRepMap = new Map<
      string,
      {
        submitted: number;
        approved: number;
        rejected: number;
        totalCredit: number;
        approvedCount: number;
      }
    >();

    for (const app of filteredData) {
      const reps: string[] =
        Array.isArray(app.assignedSalesRep) && app.assignedSalesRep.length > 0
          ? app.assignedSalesRep.map((id: string) => salesRepIdToName.get(id) ?? id)
          : ['Unassigned'];

      for (const rep of reps) {
        if (!salesRepMap.has(rep)) {
          salesRepMap.set(rep, {
            submitted: 0,
            approved: 0,
            rejected: 0,
            totalCredit: 0,
            approvedCount: 0,
          });
        }
        const entry = salesRepMap.get(rep);
        if (!entry) continue;
        entry.submitted += 1;
        if (app.status === 'APPROVED') {
          entry.approved += 1;
          if (Number(app.amountAproved) > 0) {
            entry.totalCredit += Number(app.amountAproved);
            entry.approvedCount += 1;
          }
        } else if (app.status === 'REJECTED') {
          entry.rejected += 1;
        }
      }
    }

    const salesRepSummary = Array.from(salesRepMap.entries()).map(([name, s]) => ({
      name,
      submitted: s.submitted,
      approved: s.approved,
      rejected: s.rejected,
      approvalRate: s.submitted > 0 ? s.approved / s.submitted : 0,
      averageCreditApproved: s.approvedCount > 0 ? s.totalCredit / s.approvedCount : 0,
    }));

    return {
      totalApplications,
      approvedApplications,
      newApplicationsLastMonth,
      averageCreditApproved,
      timeToDecision: averageTimeToDecision,
      approvalRate: totalApplications ? approvedApplications / totalApplications : 0,
      recentDecisions,
      yearMonthlySummary,
      salesRepSummary,
    };
  }, [
    supplierCreditApplicationsAnnualReport,
    filters,
    approvedDateRange,
    selectedYear,
    supplierBranches,
  ]);

  // Fetch data when component mounts or year changes
  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(
        getSupplierCreditApplicationsReport({
          supplierId: currentSupplier.id,
          year: selectedYear,
        })
      );
    }
  }, [dispatch, currentSupplier?.id, selectedYear]);

  return (
    <div className="flex flex-col w-full gap-4">
      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Applications Card */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Overall Credit Apps</h3>
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">{compileData.totalApplications}</p>
          <p className="text-sm text-gray-500">Total Applications</p>
        </div>

        {/* Approved Applications Card */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Approved Credit Apps</h3>
            <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">
            {compileData.approvedApplications}
          </p>
          <p className="text-sm text-gray-500">Approved Applications</p>
        </div>

        {/* New Applications Last Month Card */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">New Apps Last Month</h3>
            <div className="w-8 h-8 bg-yellow-50 rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">
            {compileData.newApplicationsLastMonth}
          </p>
          <p className="text-sm text-gray-500">New Applications</p>
        </div>

        {/* Average Credit Approved Card */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Average Credit Approved</h3>
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">
            {formatMoney(compileData.averageCreditApproved, 2)}
          </p>
          <p className="text-sm text-gray-500">Average Credit Amount</p>
        </div>

        {/* Time to Decision Card */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Time To Decision</h3>
            <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">
            {`${convertMilliseconds(compileData.timeToDecision).value} ${
              convertMilliseconds(compileData.timeToDecision).unit
            }`}
          </p>
          <p className="text-sm text-gray-500">Average Processing Time</p>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Recent Credit Decisions */}
        <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-4 hover:shadow-md transition-shadow duration-300 col-span-1 xl:col-span-1">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
            Recent Credit Decisions
          </h3>
          <div className="space-y-4">
            {compileData.recentDecisions.map((decision) => (
              <div
                key={decision.id}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 flex-shrink-0 rounded-full ${decision.color} flex items-center justify-center text-white text-sm font-medium uppercase`}>
                    {decision.initials}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm capitalize">
                      {decision.company}
                    </p>
                    <p className="text-xs text-gray-500">
                      {decision.amount ? formatMoney(decision.amount, 2) : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      decision.status === 'Approved'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                    {decision.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Applications Trend Chart */}
        <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-4 hover:shadow-md transition-shadow duration-300 col-span-1 xl:col-span-2">
          <ApplicationsTrendChart
            selectedYear={selectedYear}
            monthlyData={compileData.yearMonthlySummary || []}
          />
        </div>
      </div>
      {/* ── Row 3: Sales Rep + Recent Decisions ──────────────────────── */}
      <div className="w-full gap-4 ">
        {/* Sales rep table — takes priority, wider */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-4 xl:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg  sm:text-xl font-semibold text-gray-900">
                Performance by Sales Rep
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">{selectedYear} · click headers to sort</p>
            </div>
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-blue-50 text-blue-600">
              {compileData.salesRepSummary?.length ?? 0} reps
            </span>
          </div>
          <SalesRepBreakdownTable data={compileData.salesRepSummary || []} />
        </div>
      </div>
      {/* Credit Amounts Analysis Chart */}
      <div>
        <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-4 hover:shadow-md transition-shadow duration-300">
          <CreditAmountsChart
            selectedYear={selectedYear}
            monthlyData={compileData.yearMonthlySummary || []}
          />
        </div>
      </div>
      {/* Time to Decision Analysis Chart */}
      <div>
        <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-4 hover:shadow-md transition-shadow duration-300">
          <TimeToDecisionChart
            selectedYear={selectedYear}
            monthlyData={compileData.yearMonthlySummary || []}
          />
        </div>
      </div>{' '}
      {/* Floating Export Button */}
      {APP_ENVIRONMENT !== 'production' && (
        <AppButton
          text="Export"
          type="PRIMARY"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
          buttonStyles={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            borderRadius: '9999px',
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            zIndex: 50,
          }}
        />
      )}
    </div>
  );
}

export default CreditApplicationsReports;
